import React from 'react';
import './ServerErrorBanner.css';

export function formatServerError(err, source = 'Server') {
  if (!err) return { source, message: 'Unknown error' };
  if (typeof err === 'string') return { source, message: err };
  const resp = err.response;
  if (resp?.data?.error) return { source, message: String(resp.data.error) };
  if (resp?.data?.message) return { source, message: String(resp.data.message) };
  if (resp?.status) {
    const detail = resp.data?.details || resp.statusText || 'Request failed';
    return { source, message: `HTTP ${resp.status}: ${detail}` };
  }
  if (err.code === 'ECONNABORTED') return { source, message: 'Request timed out — server not responding' };
  if (err.code === 'ERR_NETWORK') return { source, message: 'Network error — cannot reach server' };
  if (err.message) return { source, message: err.message };
  return { source, message: 'Connection failed' };
}

export function useServerErrors() {
  const [errors, setErrors] = React.useState([]);

  const pushError = React.useCallback((source, errOrMsg) => {
    const formatted = typeof errOrMsg === 'string'
      ? { source, message: errOrMsg }
      : formatServerError(errOrMsg, source);
    const id = `${formatted.source}::${formatted.message}`;
    setErrors((prev) => {
      const next = prev.filter((e) => e.id !== id);
      return [{ id, source: formatted.source, message: formatted.message, at: Date.now() }, ...next].slice(0, 10);
    });
  }, []);

  const mergeBackendErrors = React.useCallback((backendList = []) => {
    if (!backendList?.length) return;
    setErrors((prev) => {
      const map = new Map(prev.map((e) => [e.id, e]));
      for (const row of backendList) {
        if (!row?.message) continue;
        const source = row.source || 'Server';
        const id = `${source}::${row.message}`;
        map.set(id, { id, source, message: row.message, at: row.at || Date.now(), fromBackend: true });
      }
      return [...map.values()]
        .sort((a, b) => (b.at || 0) - (a.at || 0))
        .slice(0, 10);
    });
  }, []);

  const clearSource = React.useCallback((source) => {
    setErrors((prev) => prev.filter((e) => e.source !== source));
  }, []);

  const dismiss = React.useCallback((id) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearAll = React.useCallback(() => setErrors([]), []);

  return { errors, pushError, mergeBackendErrors, clearSource, dismiss, clearAll };
}

export default function ServerErrorBanner({ errors = [], onDismiss, onClearAll }) {
  if (!errors.length) return null;

  return (
    <div className="server-error-banner" role="alert" aria-live="assertive">
      <div className="server-error-banner-head">
        <strong>Server error</strong>
        <span>{errors.length} issue{errors.length === 1 ? '' : 's'}</span>
        {onClearAll && (
          <button type="button" className="server-error-dismiss-all" onClick={onClearAll}>
            Dismiss all
          </button>
        )}
      </div>
      <ul className="server-error-list">
        {errors.map((e) => (
          <li key={e.id} className="server-error-item">
            <div className="server-error-body">
              <em>{e.source}</em>
              <span>{e.message}</span>
            </div>
            {onDismiss && (
              <button type="button" className="server-error-dismiss" onClick={() => onDismiss(e.id)} aria-label="Dismiss">
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
